/**
 * Constantes centralizadas para pipelines do CorelDRAW.
 * 
 * Este arquivo centraliza todos os valores de delays, timeouts e configurações
 * relacionadas às pipelines, facilitando ajustes e manutenção.
 */

/**
 * Delays padrão entre etapas da pipeline de preparação de pedidos.
 * 
 * Justificativas:
 * - afterOrganize (200ms): Aguarda o CorelDRAW aplicar as mudanças de organização de camadas
 *   antes de prosseguir para a distribuição por páginas.
 * - afterDistribute (200ms): Aguarda o CorelDRAW criar/mover páginas e estabilizar o documento
 *   antes de iniciar o monitoramento.
 * - afterDistributeUIStabilization (1000ms): Aguarda estabilização completa da UI do CorelDRAW
 *   após criar/mover páginas. Valor maior necessário para operações complexas de UI.
 * - afterMonitorInit (200ms): Aguarda o monitor processar o ACK e estabilizar antes de verificar status.
 * - afterMonitorStatus (200ms): Aguarda o status ser processado antes de solicitar o sync.
 * 
 * Esses delays são necessários porque:
 * 1. O CorelDRAW é uma aplicação desktop que precisa processar comandos de forma síncrona
 * 2. A comunicação via WebView2/C# tem latência variável
 * 3. Operações de UI do CorelDRAW (criar páginas, mover objetos) precisam de tempo para completar
 */
export const PIPELINE_DELAYS = {
  /** Delay após organizar camadas (ms) */
  AFTER_ORGANIZE: 200,
  /** Delay após distribuir por páginas (ms) */
  AFTER_DISTRIBUTE: 200,
  /** Delay após distribuir por páginas para estabilização UI completa (ms) - usado em SalesOrdersPage */
  AFTER_DISTRIBUTE_UI_STABILIZATION: 1000,
  /** Delay após iniciar monitoramento (ms) - aumentado para 2000ms para garantir estabilização */
  AFTER_MONITOR_INIT: 2000,
  /** Delay após verificar status do monitor (ms) - aumentado para 2000ms para garantir estabilização */
  AFTER_MONITOR_STATUS: 2000,
  /** Delay após sincronizar objetos (ms) - essencial para CorelDRAW processar snapshot antes de renderizar UI */
  AFTER_MONITOR_SYNC: 500,
} as const;

/**
 * Delays para pipeline de orçamento rápido (quotation).
 * 
 * Esta pipeline é simplificada (sem distribuição por páginas) e usada para
 * cálculo de orçamentos avulsos diretamente do documento aberto.
 * 
 * Justificativas:
 * - AFTER_ORGANIZE (1000ms): Delay maior para estabilização completa após organizar
 * - AFTER_MONITOR_INIT (2000ms): Aguarda monitor estabilizar
 * - AFTER_MONITOR_STATUS (2000ms): Aguarda status ser processado
 * - AFTER_MONITOR_SYNC (500ms): Aguarda snapshot ser processado
 */
export const QUOTATION_PIPELINE_DELAYS = {
  /** Delay após organizar camadas (ms) - maior para estabilização */
  AFTER_ORGANIZE: 1000,
  /** Delay após sincronizar objetos (ms) - initForSales já cuida do resto */
  AFTER_MONITOR_SYNC: 500,
} as const;

/**
 * Timeouts para operações da pipeline.
 */
export const PIPELINE_TIMEOUTS = {
  /** Timeout para operação de organizar camadas (ms) */
  ORGANIZE: 30000, // 30s
  /** Timeout para operação de distribuir por páginas (ms) */
  DISTRIBUTE: 30000, // 30s
  /** Timeout para operação de coletar metadados (ms) */
  COLLECT: 30000, // 30s
} as const;

/**
 * TTL (Time To Live) para snapshots e flags de pipeline.
 *
 * Os valores estendidos foram ampliados (30s → 120s) porque o snapshot precisa
 * sobreviver ao tempo que o CorelDRAW leva para abrir/renderizar o documento e
 * o usuário levar até aplicar — em máquinas/arquivos lentos 30s expirava antes.
 * VALIDATION deve ser >= o TTL usado na escrita para não rejeitar snapshot válido.
 */
export const PIPELINE_TTL = {
  /** TTL padrão para snapshot de objetos organizados (ms) */
  SNAPSHOT_DEFAULT: 15 * 1000, // 15s
  /** TTL estendido para snapshot (usado em alguns fluxos) (ms) */
  SNAPSHOT_EXTENDED: 120 * 1000, // 120s
  /** TTL para validação de snapshot ao aplicar (ms) - usado em OrderDetailsWindow */
  SNAPSHOT_VALIDATION: 120 * 1000, // 120s
} as const;

/**
 * Limites de payload das pipelines.
 */
export const PIPELINE_LIMITS = {
  /**
   * Tamanho máximo estimado do snapshot persistido em sessionStorage (bytes).
   * Proteção contra QuotaExceededError. Acima disso o pipeline aborta com
   * `snapshot_too_large`. Estimativa = string.length * 2 (UTF-16).
   */
  SNAPSHOT_MAX_SIZE_BYTES: 5 * 1024 * 1024, // 5MB
} as const;

/**
 * Delays para operações gerais (não específicas da pipeline de preparação).
 * 
 * Justificativas:
 * - AFTER_MONITOR_STOP (200ms): Aguarda o monitor processar o comando de parada e limpar estado
 * - AFTER_LOCK_STOP (200ms): Aguarda o lock-document processar o desbloqueio
 * - BEFORE_LOCK_START (100ms): Aguarda estabilização antes de iniciar lock
 * - AFTER_LOCK_START (100ms): Aguarda o lock-document processar o bloqueio
 * - PRODUCTION_MONITOR_INIT (1000ms): Aguarda monitor processar após iniciar em Production (mais conservador)
 * - PRODUCTION_AFTER_STATUS (500ms): Aguarda após verificar status em Production
 * - PRODUCTION_BEFORE_SYNC (500ms): Aguarda antes de sincronizar em Production
 */
export const OPERATION_DELAYS = {
  /** Delay após parar monitoramento (ms) */
  AFTER_MONITOR_STOP: 200,
  /** Delay após parar lock-document (ms) */
  AFTER_LOCK_STOP: 200,
  /** Delay antes de iniciar lock-document (ms) */
  BEFORE_LOCK_START: 100,
  /** Delay após iniciar lock-document (ms) */
  AFTER_LOCK_START: 100,
  /** Delay mínimo para estabilização geral (ms) */
  MIN_STABILIZATION: 100,
  /** Delay após abrir arquivo (ms) - CorelDRAW precisa processar/renderizar o documento */
  AFTER_OPEN_FILE: 2500,
  /** Delay reduzido antes de verificar status (ms) */
  BEFORE_STATUS_CHECK: 50,
  /** Delay para finalização de operações (ms) */
  FINALIZATION: 100,
  /** Delay após iniciar monitor em Production (ms) - aumentado para 2500ms para evitar race condition com evento documento-trocado */
  PRODUCTION_MONITOR_INIT: 2500,
  /** Delay após verificar status em Production (ms) - aguarda status estabilizar */
  PRODUCTION_AFTER_STATUS: 1000,
  /** Delay antes de sincronizar em Production (ms) - aguarda objetos serem processados */
  PRODUCTION_BEFORE_SYNC: 1000,
  /** Delay após sincronizar em Production (ms) - aguarda snapshot ser processado */
  PRODUCTION_AFTER_SYNC: 1000,
  /** Delay antes de fechar modal após concluído (ms) */
  PRODUCTION_BEFORE_CLOSE: 500,
} as const;

/**
 * Helper para criar um delay padronizado.
 * 
 * @param ms - Milissegundos para aguardar
 * @param reason - Razão do delay (para logs)
 */
export function pipelineSleep(ms: number, reason?: string): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve();
    }, ms);
  });
}

/**
 * TTL para sinais de abort de operações.
 */
export const ABORT_TTL = {
  /** TTL padrão para sinal de abort (ms) */
  DEFAULT: 60 * 1000, // 1 minuto
} as const;

/**
 * Thresholds para eventos de monitoramento.
 * Usados para filtrar eventos atrasados ou inválidos.
 */
export const MONITOR_EVENT_THRESHOLDS = {
  /** Ignora eventos se monitor foi iniciado há menos de X ms (proteção contra eventos atrasados) */
  RECENT_INIT_MS: 2000,
  /** Ignora eventos mais antigos que X ms (proteção contra eventos muito antigos) */
  OLD_EVENT_MS: 5000,
} as const;

/**
 * Configurações padrão de retry para etapas da pipeline.
 * 
 * Justificativas:
 * - maxRetries (3): Balance entre resiliência e tempo total
 * - baseDelayMs (1000ms): Delay inicial suficiente para estabilização
 * - maxDelayMs (10000ms): Limite máximo para evitar delays excessivos
 * - backoffFactor (2): Backoff exponencial padrão (1s, 2s, 4s)
 */
export const PIPELINE_RETRY_CONFIG = {
  /** Número máximo de tentativas por padrão */
  DEFAULT_MAX_RETRIES: 3,
  /** Delay base entre tentativas em ms - aumentado para 2000ms para monitorar-objetos */
  DEFAULT_BASE_DELAY_MS: 2000,
  /** Delay máximo entre tentativas em ms */
  DEFAULT_MAX_DELAY_MS: 10000,
  /** Fator de backoff exponencial */
  DEFAULT_BACKOFF_FACTOR: 2,
} as const;

